import React, { useState, useMemo } from "react";
import {
  Box,
  VStack,
  HStack,
  SimpleGrid,
  Button,
  Flex,
  Heading,
  Text,
  useColorModeValue,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Badge,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Progress,
  Select,
  Spinner,
  Tooltip,
  IconButton,
} from "@chakra-ui/react";
import {
  FiDollarSign,
  FiAlertTriangle,
  FiInfo,
  FiCalendar,
  FiRefreshCw,
  FiCheckCircle,
  FiTrendingDown,
} from "react-icons/fi";
import { useGetSettlementBreakdown } from "../../../services/payrollService";
import { useGetPlanInfo } from "../../../services/planService";
import { formatCurrency, getCurrencySymbol } from "../../../utils/currency";

const SettlementBreakdown = () => {
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const { planInfo } = useGetPlanInfo();

  // Prepare filter params
  const filterParams = useMemo(() => {
    if (selectedMonth && selectedYear) {
      return { month: selectedMonth, year: selectedYear };
    }
    return {}; // Default: last 6 months
  }, [selectedMonth, selectedYear]);

  // Fetch settlement breakdown data
  const {
    data: settlementData,
    isLoading,
    error,
    refetch,
  } = useGetSettlementBreakdown(filterParams);

  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const cardBg = useColorModeValue("white", "gray.700");

  // Generate month options
  const months = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" },
  ];

  // Generate year options (last 5 years to current year)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 6 }, (_, i) => currentYear - i);

  // Handle filter changes
  const handleMonthChange = (e) => {
    const month = e.target.value ? parseInt(e.target.value) : null;
    setSelectedMonth(month);
    // If month is selected but no year, default to current year
    if (month && !selectedYear) {
      setSelectedYear(currentYear);
    }
  };

  const handleYearChange = (e) => {
    const year = e.target.value ? parseInt(e.target.value) : null;
    setSelectedYear(year);
    // If year is selected but no month, default to current month
    if (year && !selectedMonth) {
      setSelectedMonth(new Date().getMonth() + 1);
    }
  };

  const clearFilters = () => {
    setSelectedMonth(null);
    setSelectedYear(null);
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  // Check if due date is soon (within 7 days)
  const isDueSoon = (dueDate) => {
    if (!dueDate) return false;
    try {
      const due = new Date(dueDate);
      const today = new Date();
      const diffTime = due - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays >= 0 && diffDays <= 7;
    } catch {
      return false;
    }
  };

  // Check if due date is overdue
  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    try {
      const due = new Date(dueDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      due.setHours(0, 0, 0, 0);
      return due < today;
    } catch {
      return false;
    }
  };

  // Get category color scheme
  const getCategoryColor = (category) => {
    switch (category?.toLowerCase()) {
      case "tax":
        return "red";
      case "statutory":
        return "orange";
      case "employee":
        return "blue";
      default:
        return "gray";
    }
  };

  // Get status color
  const getStatusColor = (status, dueDate) => {
    if (isOverdue(dueDate)) return "red";
    if (isDueSoon(dueDate)) return "orange";
    if (status === "pending") return "yellow";
    if (status === "paid") return "green";
    return "gray";
  };

  if (error) {
    return (
      <Alert status="error" borderRadius="lg">
        <AlertIcon />
        <Box flex="1">
          <AlertTitle>Failed to load settlement breakdown</AlertTitle>
          <AlertDescription>
            {error.message || "Unable to retrieve settlement information. Please try again."}
          </AlertDescription>
        </Box>
        <Button size="sm" onClick={() => refetch()}>
          Retry
        </Button>
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <Box textAlign="center" py={12}>
        <Spinner size="xl" color="blue.500" thickness="4px" />
        <Text mt={4} color="gray.600">
          Loading settlement breakdown...
        </Text>
      </Box>
    );
  }

  if (!settlementData?.settlement_breakdown) {
    return (
      <Alert status="info" borderRadius="lg">
        <AlertIcon />
        <AlertDescription>
          No settlement data available for the selected period.
        </AlertDescription>
      </Alert>
    );
  }

  const walletInfo = settlementData;
  const breakdown = settlementData.settlement_breakdown;
  const filterPeriod = settlementData.filter_period;

  // Calculate available balance (use available_balance from API, fallback to calculated value)
  const availableBalance = walletInfo.available_balance ?? 
    ((walletInfo.balance ?? walletInfo.current_balance ?? 0) - (breakdown.total_settlement_amount || 0));

  // Calculate percentages for category breakdown
  const totalSettlement = breakdown.total_settlement_amount || 0;
  const taxPercentage =
    totalSettlement > 0
      ? ((breakdown.settlement_summary?.tax_obligations || 0) / totalSettlement) * 100
      : 0;
  const statutoryPercentage =
    totalSettlement > 0
      ? ((breakdown.settlement_summary?.statutory_deductions || 0) / totalSettlement) * 100
      : 0;
  const employeePercentage =
    totalSettlement > 0
      ? ((breakdown.settlement_summary?.employee_deductions || 0) / totalSettlement) * 100
      : 0;

  return (
    <VStack spacing={6} align="stretch">
      {/* Filter and Period Header */}
      <Flex justify="space-between" align="center" flexWrap="wrap" gap={4}>
        <Box>
          <Heading size="md" color="gray.800" mb={1}>
            Settlement Breakdown
          </Heading>
          {filterPeriod && (
            <Text fontSize="sm" color="gray.600">
              {filterPeriod.type === "last_6_months"
                ? "Showing: Last 6 months"
                : `Showing: ${filterPeriod.month_name} ${filterPeriod.year}`}
            </Text>
          )}
        </Box>
        <HStack spacing={3}>
          <Select
            placeholder="Select Month"
            value={selectedMonth || ""}
            onChange={handleMonthChange}
            width="150px"
            size="md"
          >
            {months.map((month) => (
              <option key={month.value} value={month.value}>
                {month.label}
              </option>
            ))}
          </Select>
          <Select
            placeholder="Select Year"
            value={selectedYear || ""}
            onChange={handleYearChange}
            width="120px"
            size="md"
          >
            {years.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </Select>
          {(selectedMonth || selectedYear) && (
            <Button size="sm" variant="ghost" onClick={clearFilters}>
              Clear
            </Button>
          )}
          <IconButton
            icon={<FiRefreshCw />}
            onClick={() => refetch()}
            size="md"
            variant="outline"
            aria-label="Refresh"
          />
        </HStack>
      </Flex>

      {/* Available Balance Card */}
      <Box bg={cardBg} p={6} borderRadius="lg" border="1px solid" borderColor={borderColor}>
        <VStack spacing={4} align="stretch">
          <HStack justify="space-between">
            <Heading size="sm" color="gray.700">
              Available Balance for Payroll
            </Heading>
            <Badge colorScheme="green" fontSize="md" px={3} py={1}>
              Available
            </Badge>
          </HStack>
          <Stat>
            <StatNumber fontSize="3xl" color="green.500">
              {formatCurrency(availableBalance, planInfo?.country)}
            </StatNumber>
            <StatHelpText>
              Available Balance: {formatCurrency(walletInfo.available_balance ?? walletInfo.balance ?? walletInfo.current_balance ?? 0, planInfo?.country)}
              {walletInfo.pending_settlement !== undefined && walletInfo.pending_settlement > 0 && ` (Pending: ${formatCurrency(walletInfo.pending_settlement, planInfo?.country)})`}
              {breakdown.total_settlement_amount > 0 && ` - Settlement Obligations: ${formatCurrency(breakdown.total_settlement_amount || 0, planInfo?.country)}`}
            </StatHelpText>
          </Stat>
        </VStack>
      </Box>

      {/* Summary Cards */}
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
        {/* Total Settlement */}
        <Box bg={cardBg} p={6} borderRadius="lg" border="1px solid" borderColor={borderColor}>
          <Stat>
            <VStack spacing={2} align="stretch">
              <HStack justify="space-between">
                <StatLabel fontSize="sm" color="gray.600">
                  Total Settlement
                </StatLabel>
                <Badge colorScheme="purple" fontSize="xs">
                  All Deductions
                </Badge>
              </HStack>
              <StatNumber fontSize="2xl" color="purple.500">
                {formatCurrency(breakdown.total_settlement_amount || 0, planInfo?.country)}
              </StatNumber>
              <StatHelpText fontSize="xs" color="gray.500">
                Tax + Deductions in wallet
              </StatHelpText>
            </VStack>
          </Stat>
        </Box>

        {/* Immediate Payable */}
        <Box
          bg={cardBg}
          p={6}
          borderRadius="lg"
          border="1px solid"
          borderColor="orange.200"
        >
          <Stat>
            <VStack spacing={2} align="stretch">
              <HStack justify="space-between">
                <StatLabel fontSize="sm" color="gray.600">
                  Immediate Payable
                </StatLabel>
                <Badge colorScheme="orange" fontSize="xs">
                  Must Pay
                </Badge>
              </HStack>
              <StatNumber fontSize="2xl" color="orange.500">
                {formatCurrency(
                  breakdown.settlement_summary?.total_immediate_payable || 0,
                  planInfo?.country
                )}
              </StatNumber>
              <StatHelpText fontSize="xs" color="gray.500">
                Tax + Statutory (Required)
              </StatHelpText>
            </VStack>
          </Stat>
        </Box>

        {/* Optional Payable */}
        <Box bg={cardBg} p={6} borderRadius="lg" border="1px solid" borderColor={borderColor}>
          <Stat>
            <VStack spacing={2} align="stretch">
              <HStack justify="space-between">
                <StatLabel fontSize="sm" color="gray.600">
                  Optional Payable
                </StatLabel>
                <Badge colorScheme="blue" fontSize="xs">
                  Optional
                </Badge>
              </HStack>
              <StatNumber fontSize="2xl" color="blue.500">
                {formatCurrency(
                  breakdown.settlement_summary?.total_optional_payable || 0,
                  planInfo?.country
                )}
              </StatNumber>
              <StatHelpText fontSize="xs" color="gray.500">
                Employee Deductions
              </StatHelpText>
            </VStack>
          </Stat>
        </Box>
      </SimpleGrid>

      {/* Category Breakdown */}
      {totalSettlement > 0 && (
        <Box bg={cardBg} p={6} borderRadius="lg" border="1px solid" borderColor={borderColor}>
          <VStack spacing={4} align="stretch">
            <Heading size="sm" color="gray.700">
              Breakdown by Category
            </Heading>
            <VStack spacing={4} align="stretch">
              {/* Tax Obligations */}
              <Box>
                <HStack justify="space-between" mb={2}>
                  <HStack spacing={2}>
                    <Badge colorScheme="red">Tax</Badge>
                    <Text fontSize="sm" fontWeight="medium" color="gray.700">
                      Tax Obligations
                    </Text>
                  </HStack>
                  <Text fontSize="sm" fontWeight="600" color="red.600">
                    {formatCurrency(
                      breakdown.settlement_summary?.tax_obligations || 0,
                      planInfo?.country
                    )}
                  </Text>
                </HStack>
                <Progress
                  value={taxPercentage}
                  colorScheme="red"
                  size="lg"
                  borderRadius="full"
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  {taxPercentage.toFixed(1)}% of total settlement
                </Text>
              </Box>

              {/* Statutory Deductions */}
              <Box>
                <HStack justify="space-between" mb={2}>
                  <HStack spacing={2}>
                    <Badge colorScheme="orange">Statutory</Badge>
                    <Text fontSize="sm" fontWeight="medium" color="gray.700">
                      Statutory Deductions
                    </Text>
                  </HStack>
                  <Text fontSize="sm" fontWeight="600" color="orange.600">
                    {formatCurrency(
                      breakdown.settlement_summary?.statutory_deductions || 0,
                      planInfo?.country
                    )}
                  </Text>
                </HStack>
                <Progress
                  value={statutoryPercentage}
                  colorScheme="orange"
                  size="lg"
                  borderRadius="full"
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  {statutoryPercentage.toFixed(1)}% of total settlement
                </Text>
              </Box>

              {/* Employee Deductions */}
              <Box>
                <HStack justify="space-between" mb={2}>
                  <HStack spacing={2}>
                    <Badge colorScheme="blue">Employee</Badge>
                    <Text fontSize="sm" fontWeight="medium" color="gray.700">
                      Employee Deductions
                    </Text>
                  </HStack>
                  <Text fontSize="sm" fontWeight="600" color="blue.600">
                    {formatCurrency(
                      breakdown.settlement_summary?.employee_deductions || 0,
                      planInfo?.country
                    )}
                  </Text>
                </HStack>
                <Progress
                  value={employeePercentage}
                  colorScheme="blue"
                  size="lg"
                  borderRadius="full"
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  {employeePercentage.toFixed(1)}% of total settlement
                </Text>
              </Box>
            </VStack>
          </VStack>
        </Box>
      )}

      {/* Detailed Breakdown Table */}
      <Box bg={cardBg} p={6} borderRadius="lg" border="1px solid" borderColor={borderColor}>
        <VStack spacing={4} align="stretch">
          <HStack justify="space-between">
            <Heading size="sm" color="gray.700">
              Deduction Breakdown
            </Heading>
            <Badge colorScheme="gray" fontSize="xs">
              {breakdown.pending_payments?.length || 0} items
            </Badge>
          </HStack>

          {breakdown.pending_payments && breakdown.pending_payments.length > 0 ? (
            <TableContainer>
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>Deduction Type</Th>
                    <Th>Description</Th>
                    <Th isNumeric>Amount</Th>
                    <Th>Category</Th>
                    <Th>Due Date</Th>
                    <Th>Status</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {breakdown.pending_payments.map((item, index) => {
                    const categoryColor = getCategoryColor(item.category);
                    const statusColor = getStatusColor(item.status, item.due_date);
                    const dueSoon = isDueSoon(item.due_date);
                    const overdue = isOverdue(item.due_date);

                    return (
                      <Tr key={index}>
                        <Td>
                          <Text fontSize="sm" fontWeight="500">
                            {item.deduction_type || "N/A"}
                          </Text>
                        </Td>
                        <Td>
                          <Text fontSize="sm" color="gray.600">
                            {item.description || "N/A"}
                          </Text>
                        </Td>
                        <Td isNumeric>
                          <Text fontSize="sm" fontWeight="600">
                            {formatCurrency(item.amount || 0, planInfo?.country)}
                          </Text>
                        </Td>
                        <Td>
                          <Badge colorScheme={categoryColor} fontSize="xs">
                            {item.category || "N/A"}
                          </Badge>
                        </Td>
                        <Td>
                          <Tooltip
                            label={
                              overdue
                                ? "This payment is overdue"
                                : dueSoon
                                ? "Due within 7 days"
                                : ""
                            }
                          >
                            <HStack spacing={1}>
                              {overdue && <FiAlertTriangle color="red" size={14} />}
                              {dueSoon && !overdue && <FiCalendar color="orange" size={14} />}
                              <Text
                                fontSize="sm"
                                color={overdue ? "red.600" : dueSoon ? "orange.600" : "gray.600"}
                                fontWeight={overdue || dueSoon ? "600" : "normal"}
                              >
                                {formatDate(item.due_date)}
                              </Text>
                            </HStack>
                          </Tooltip>
                        </Td>
                        <Td>
                          <Badge
                            colorScheme={statusColor}
                            fontSize="xs"
                            variant={overdue ? "solid" : "subtle"}
                          >
                            {overdue ? "Overdue" : dueSoon ? "Due Soon" : item.status || "Pending"}
                          </Badge>
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </TableContainer>
          ) : (
            <Alert status="info" borderRadius="lg">
              <AlertIcon />
              <AlertDescription>
                No pending payments found for the selected period.
              </AlertDescription>
            </Alert>
          )}
        </VStack>
      </Box>
    </VStack>
  );
};

export default SettlementBreakdown;

